// react
import { useState } from 'react';

// third-party
import { LoadingButton } from '@mui/lab';
import { Button, CardContent, DialogTitle, Divider, InputLabel, Stack, TextField } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';

// project import
import VenueTypeDto from 'common/dto-types/venue-type-dto';
import RequiredAsterisk from 'components/RequiredAsterisk';
import { dispatch, useSelector } from 'store';
import { closeForm } from 'store/reducers/venue-type';
import { openSnackbar } from '../../store/reducers/snackbar';
import { createVenueType, updateVenueType } from './api';

const NAME_MAX_LENGTH = 200;

type VenueTypeFormProps = {
    refreshCallback: () => Promise<void>;
};

const VenueTypeForm = ({ refreshCallback }: VenueTypeFormProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const data = useSelector((state) => state.venueType.data);
    const isCreating = !data;

    const initialValues: VenueTypeDto = {
        id: data?.id ?? 0,
        name: data?.name ?? '',
    };

    const validationSchema = yup.object({
        name: yup
            .string()
            .trim()
            .required('Name is required')
            .max(NAME_MAX_LENGTH, `Name cannot exceed ${NAME_MAX_LENGTH} characters`),
    });

    const handleVenueTypeSubmit = async (value: VenueTypeDto) => {
        setIsLoading(true);
        const dto: VenueTypeDto = { id: value.id, name: value.name.trim() };
        let response: Awaited<ReturnType<typeof createVenueType>>;
        let successMessage = '';
        let failedMessage = '';

        if (isCreating) {
            response = await createVenueType(dto);
            successMessage = 'Venue Type created successfully!';
            failedMessage = 'Failed to create Venue Type';
        } else {
            response = await updateVenueType(dto);
            successMessage = 'Venue Type updated successfully!';
            failedMessage = 'Failed to update Venue Type';
        }

        if (response?.status === 200) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: successMessage,
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );
            refreshCallback();
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: response?.data?.message ?? failedMessage,
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
        }
        setIsLoading(false);
    };

    const formik = useFormik<VenueTypeDto>({
        initialValues,
        validationSchema,
        onSubmit: handleVenueTypeSubmit,
    });

    const { errors, touched, dirty, isValid, getFieldProps, handleSubmit } = formik;

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
                <DialogTitle>{isCreating ? 'Add' : 'Update'} Venue Type</DialogTitle>
                <CardContent>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <InputLabel>
                                Name <RequiredAsterisk />
                            </InputLabel>
                            <TextField
                                fullWidth
                                id="name"
                                placeholder="Enter Venue Type Name"
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                {...getFieldProps('name')}
                            />
                        </Stack>
                    </Stack>
                </CardContent>
                <Divider />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
                    <Button color="error" size="small" disabled={isLoading} onClick={() => dispatch(closeForm())}>
                        Cancel
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        size="small"
                        loading={isLoading}
                        disabled={!isValid || (isCreating && !dirty)}
                    >
                        Save
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};

export default VenueTypeForm;
